import React, { useEffect, useState } from "react";

import { T } from "services/lang/LanguageService";
import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import { defaultLoadConf } from "components/dashboard/data-input-output-table/defaultConfigs";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { DatasetLoadingConf, DatasetPieProps } from "../interfaces";
import DataTableComponent from "./DataTableComponent";
import defaultDatasetPieChartConfig from "./pieChartDefaultConfigs";
import { DashboardEditorProps } from "components/ComponentTypeTypes";

interface PieChart {
  identifiers?: string[];
  chartName?: string;
  color?: string;
}

function DatasetPieChartEditor({ config, onConfigChange }: DashboardEditorProps<DatasetPieProps>) {
  let conf = config ?? defaultDatasetPieChartConfig;
  if (Object.keys(conf).length === 0) {
    conf = defaultDatasetPieChartConfig;
  }

  const [currentConfig, setCurrentConfig] = useState(conf);
  const [currentCenterTextConfig, setCurrentCenterTextConfig] = useState(conf.centerTextConfig);
  const [currentChartsArray, setCurrentChartsArray] = useState<PieChart[]>(conf.charts);

  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([]);

  useEffect(() => {
    const tempIdentifiers = currentConfig.datasets?.map(dataset => dataset.identifier);
    if (tempIdentifiers) {
      setConfigPropertyState("identifiers", tempIdentifiers);
    }
  }, [currentConfig.datasets]);

  useEffect(() => {
    const initialSelectedDatasets = currentConfig.datasets?.map(dataset => dataset.identifier);
    setSelectedDatasets(initialSelectedDatasets ?? []);
    setConfigPropertyState("charts", currentConfig.charts);
  }, []);

  useEffect(() => {
    updateCharts("charts");
  }, [currentChartsArray]);

  useEffect(() => {
    onConfigChange(currentConfig);
  }, [currentConfig]);

  const handleLoadConf = (e: any): void => {
    const tempConfig = structuredClone(currentConfig);
    if (e.value) {
      tempConfig.loadConf = defaultLoadConf;
    } else {
      delete tempConfig.loadConf;
    }
    setCurrentConfig(tempConfig);
  };

  const handleLoadConfChange = <K extends keyof DatasetLoadingConf>(
    id: K,
    value: DatasetLoadingConf[K] | { toString: () => string }
  ) => {
    if (!currentConfig.loadConf) {
      return;
    }

    const tempConfig = structuredClone(currentConfig);
    const loadingConfig = tempConfig.loadConf as DatasetLoadingConf;

    if (typeof value === "number") {
      loadingConfig[id] = value as DatasetLoadingConf[K];
    } else if (value && typeof value === "object" && typeof value.toString === "function") {
      loadingConfig[id] = value.toString().toUpperCase() as DatasetLoadingConf[K];
    } else {
      loadingConfig[id] = (value as string)?.toString().toUpperCase() as DatasetLoadingConf[K];
    }

    setCurrentConfig(tempConfig);
  };

  const setConfigPropertyState = (property: keyof DatasetPieProps, value: any) => {
    if (property === "charts") {
      const updatedCharts = (value || []).map((chart: PieChart, index: number) => ({
        ...chart,
        chartName: chart.chartName || `${T("word.chart")} ${index + 1}`,
      }));
      setCurrentConfig(prevConfig => ({
        ...prevConfig,
        [property]: updatedCharts,
      }));
    } else {
      setCurrentConfig(prevConfig => ({
        ...prevConfig,
        [property]: value,
      }));
    }
  };

  const updateCharts = (property: keyof DatasetPieProps) => {
    setCurrentConfig(prevConfig => ({
      ...prevConfig,
      [property]: currentChartsArray,
    }));
  };

  const handleConfigChange = (e: any): void => {
    setConfigPropertyState(e.target.id as keyof DatasetPieProps, e.target.value);
  };

  const setConfigPropertyStateForCenterTextConfig = (property: keyof DatasetPieProps, value: any) => {
    const newConfig = { ...currentCenterTextConfig, [property]: value };
    setCurrentCenterTextConfig(newConfig);
    setConfigPropertyState("centerTextConfig", newConfig);
  };

  const handleConfigChangeForCenterTextConfig = (e: any): void => {
    setConfigPropertyStateForCenterTextConfig(e.target.id as keyof DatasetPieProps, e.target.value);
  };

  const handleAddChart = () => {
    const newChart: PieChart = {
      chartName: "",
      identifiers: [],
    };

    if (currentConfig.charts?.length === 0) {
      newChart.chartName = `${T("word.chart")} 1`;
    } else {
      const lastChart: PieChart = currentConfig.charts[currentConfig.charts.length - 1];
      const lastChartNumber = parseInt(lastChart.chartName?.split(" ")[1] ?? "0", 10);
      const newChartNumber = lastChartNumber + 1;

      newChart.chartName = `${T("word.chart")} ${newChartNumber}`;
    }
    setCurrentConfig((prevConfig: any) => {
      const updatedCharts = [...prevConfig.charts, newChart];
      return { ...prevConfig, charts: updatedCharts };
    });
  };

  const handleDatasetChange = (index: number, selectedIdentifiers: string[]): void => {
    const updatedCharts = [...currentConfig.charts];

    updatedCharts[index] = {
      ...updatedCharts[index],
      identifiers: selectedIdentifiers,
    };
    setCurrentConfig(prevConfig => ({
      ...prevConfig,
      charts: updatedCharts,
    }));
    setSelectedDatasets(selectedIdentifiers);
  };

  return (
    <div>
      <div className="flex flex-column gap-3">
        <DataTableComponent
          charts={currentConfig.charts || []}
          onDataUpdate={setCurrentChartsArray}
          selectedDatasets={selectedDatasets}
          handleDatasetChange={(index, selectedIdentifiers) =>
            handleDatasetChange(index, selectedIdentifiers)
          }
          handleAddChart={handleAddChart}
        />
        <div className="flex flex-row">
          <div className="col-2 flex flex-column mb-3 gap-2">
            <label htmlFor="height">{T("word.height")}</label>
            <div className="p-inputgroup w-full">
              <InputNumber
                id="height"
                value={currentConfig.height as number}
                onValueChange={handleConfigChange}
              />
              <span className="p-inputgroup-addon">px</span>
            </div>
          </div>
          <div className="col-2 flex flex-column mb-3 gap-2">
            <label htmlFor="fontSize">{T("word.font-size")}</label>
            <div className="p-inputgroup w-full">
              <InputNumber
                id="fontSize"
                value={currentConfig.centerTextConfig?.fontSize as number}
                onValueChange={handleConfigChangeForCenterTextConfig}
              />
              <span className="p-inputgroup-addon">px</span>
            </div>
          </div>
          <div className="col-2 flex flex-column mb-3 gap-2">
            <label htmlFor="decimalPlaces">{T("config.decimal-places")}</label>
            <div className="p-inputgroup w-full">
              <InputNumber
                id="decimalPlaces"
                value={currentConfig.centerTextConfig?.decimalPlaces as number}
                onValueChange={handleConfigChangeForCenterTextConfig}
              />
            </div>
          </div>
          <div className="col-2 flex flex-column mb-3 gap-2">
            <label htmlFor="chartsPerRow">{T("config.pie-chart.charts-per-row")}</label>
            <div className="p-inputgroup w-full">
              <InputNumber
                id="chartsPerRow"
                value={currentConfig.chartsPerRow as number}
                onValueChange={handleConfigChange}
              />
            </div>
          </div>
          <div className="col-4 flex flex-column mb-3 gap-2">
            <label htmlFor="minimumRepresentableValue">
              {T("config.pie-chart.minimum-representable-value")} (%)
            </label>
            <div className="p-inputgroup w-full">
              <InputNumber
                id="minimumRepresentableValue"
                value={currentConfig.minimumRepresentableValue as number}
                onValueChange={handleConfigChange}
                maxFractionDigits={2}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-row gap-3">
          <div className="gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="loadConf">{T("load-conf.load-conf")}</label>
            <InputSwitch
              id="loadConf"
              checked={currentConfig.loadConf !== undefined}
              onChange={handleLoadConf}
            />
          </div>
          <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="hideRefresh">{T("chart-config.hide-refresh")}</label>
            <InputSwitch
              id="hideRefresh"
              checked={Boolean(currentConfig.hideRefresh)}
              onChange={handleConfigChange}
            />
          </div>
          <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="showDateSelection">{T("chart-config.show-date-selection")}</label>
            <InputSwitch
              id="showDateSelection"
              checked={Boolean(currentConfig.showDateSelection)}
              onChange={handleConfigChange}
            />
          </div>
          {currentConfig.showDateSelection && (
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
              <label htmlFor="showDateSelection">{T("action.get-most-recent")}</label>
              <InputSwitch
                id="getMostRecent"
                checked={Boolean(currentConfig.getMostRecent)}
                onChange={handleConfigChange}
              />
            </div>
          )}
          <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="hideLegend">{T("action.hide-legend")}</label>
            <InputSwitch
              id="hideLegend"
              checked={Boolean(currentConfig.hideLegend)}
              onChange={handleConfigChange}
            />
          </div>
        </div>
        {currentConfig.loadConf !== undefined && (
          <div className="flex flex-row gap-3">
            <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
              <label htmlFor="lag">{T("load-conf.lag")}</label>
              <div className="p-inputgroup">
                <InputNumber
                  id="lag"
                  value={(currentConfig.loadConf as DatasetLoadingConf).lag as number}
                  onValueChange={e =>
                    handleLoadConfChange(e.target.id as keyof DatasetLoadingConf, e.target.value as number)
                  }
                />
                <TimeUnitDropdown
                  id="lagUnit"
                  className="p-inputgroup-addon p-0"
                  value={(currentConfig.loadConf as DatasetLoadingConf)?.lagUnit as string}
                  onChange={e =>
                    handleLoadConfChange(e.target.id as keyof DatasetLoadingConf, e.target.value as number)
                  }
                />
              </div>
            </div>
            <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
              <label htmlFor="lead">{T("load-conf.lead")}</label>
              <div className="p-inputgroup">
                <InputNumber
                  id="lead"
                  value={(currentConfig.loadConf as DatasetLoadingConf).lead as number}
                  onValueChange={e =>
                    handleLoadConfChange(e.target.id as keyof DatasetLoadingConf, e.target.value as number)
                  }
                />
                <TimeUnitDropdown
                  id="leadUnit"
                  className="p-inputgroup-addon p-0"
                  value={(currentConfig.loadConf as DatasetLoadingConf)?.leadUnit as string}
                  onChange={e =>
                    handleLoadConfChange(e.target.id as keyof DatasetLoadingConf, e.target.value)
                  }
                />
              </div>
            </div>
            <div className="col-3 gap-2 flex flex-column justify-content-center align-items-center">
              <label htmlFor="alignUnit">{T("load-conf.align-unit")}</label>
              <TimeUnitDropdown
                id="alignUnit"
                className="w-full"
                value={((currentConfig.loadConf as DatasetLoadingConf)?.alignUnit as string) ?? ""}
                onChange={e =>
                  handleLoadConfChange(e.target.id as keyof DatasetLoadingConf, e.target.value as number)
                }
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DatasetPieChartEditor;
